import { useEffect, useMemo, useState } from "react"
import * as gqlTypes from "@hydrologiq/gql-experience/graphql"
import { useQueryWithEndpoint, useGraphQLClient } from "@hydrologiq/shared/hooks/useGraphQLClient"

type SystemConnection = gqlTypes.GetSystemDataConnectionsQuery["getSystemDataConnections"][number]

/**
 * The system-held connections that map at least one of the given assets.
 * Asset-held connections deployed into the system are left out: the asset
 * already shows those as its own.
 */
export function selectSystemHeldConnections(
  connections: SystemConnection[],
  mappedAssetIdsByConnection: Record<string, string[]>,
  assetIds: string[],
): SystemConnection[] {
  const wanted = new Set(assetIds)
  return connections.filter(
    (c) => !c.assetOwned && (mappedAssetIdsByConnection[c.id] ?? []).some((id) => wanted.has(id)),
  )
}

/**
 * Connections a system holds that map this asset, or one of its modules.
 *
 * Takes one call per system-held connection to find what it maps. If
 * getAssetDataConnections ever returns system-held connections, use that.
 */
export function useSystemHeldConnections(systemId: string | undefined, assetIds: string[]) {
  const client = useGraphQLClient("primary")
  const { data, loading, error } = useQueryWithEndpoint<
    gqlTypes.GetSystemDataConnectionsQuery,
    gqlTypes.GetSystemDataConnectionsQueryVariables
  >(
    gqlTypes.GetSystemDataConnectionsDocument,
    { variables: { systemId: systemId || "" }, skip: !systemId, fetchPolicy: "cache-and-network" },
    "primary",
  )

  const systemHeld = useMemo(
    () => (data?.getSystemDataConnections ?? []).filter((c) => !c.assetOwned),
    [data],
  )
  // Keyed on ids so a refetch returning the same connections doesn't reload mappings
  const heldKey = systemHeld.map((c) => c.id).join(",")
  const [mapped, setMapped] = useState<{ key: string; byConnection: Record<string, string[]> } | null>(null)

  useEffect(() => {
    if (!client || !heldKey) return
    let cancelled = false
    Promise.all(
      heldKey.split(",").map((id) =>
        client
          .query<gqlTypes.GetMappedAssetsQuery, gqlTypes.GetMappedAssetsQueryVariables>({
            query: gqlTypes.GetMappedAssetsDocument,
            variables: { monitoringInstanceId: id },
          })
          .then((r) => [id, r.data.getMappedAssets.map((m) => m.assetId)] as const),
      ),
    )
      .then((entries) => {
        if (!cancelled) setMapped({ key: heldKey, byConnection: Object.fromEntries(entries) })
      })
      .catch(() => {
        if (!cancelled) setMapped({ key: heldKey, byConnection: {} })
      })
    return () => {
      cancelled = true
    }
  }, [client, heldKey])

  const assetKey = assetIds.join(",")
  const connections = useMemo(
    () => (mapped ? selectSystemHeldConnections(systemHeld, mapped.byConnection, assetKey.split(",")) : []),
    [systemHeld, mapped, assetKey],
  )

  return {
    connections,
    loading: (loading && !data) || (!!heldKey && mapped?.key !== heldKey),
    error: error?.message,
  }
}
